import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { useLanguage } from "../lib/LanguageContext";
import { gaPhoneClick, gaEmailClick } from "../lib/analytics";
import SocialLinks from "./SocialLinks";

export default function Footer() {
    const { t } = useLanguage();
    const year = new Date().getFullYear();

    return (
        <footer className="bg-navy text-pearl" data-testid="site-footer">
            <div className="container-luxe py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
                <div className="md:col-span-5 space-y-5">
                    <img
                        src="/brand/pkm-logo-dark-bg.png"
                        alt="Pacific Key Management"
                        width="860"
                        height="256"
                        style={{ width: "auto" }}
                        className="h-16 sm:h-20 w-auto select-none"
                        draggable="false"
                        data-testid="footer-logo"
                    />
                    <p className="text-pearl/70 text-sm leading-relaxed max-w-sm">
                        {t("footer.tagline")}
                    </p>
                    <span className="gold-divider" />
                    <p className="text-pearl/55 text-xs tracking-wide">{t("footer.bilingual")}</p>
                </div>

                <div className="md:col-span-3">
                    <p className="overline text-gold/80 mb-5">{t("footer.quickLinks")}</p>
                    <ul className="space-y-3 text-sm text-pearl/75">
                        <li><Link to="/about" className="hover:text-gold transition-colors" data-testid="footer-link-about">{t("nav.about")}</Link></li>
                        <li><Link to="/services" className="hover:text-gold transition-colors" data-testid="footer-link-services">{t("nav.services")}</Link></li>
                        <li><Link to="/pacific-rentals" className="hover:text-gold transition-colors" data-testid="footer-link-rentals">{t("nav.rentals")}</Link></li>
                        <li><Link to="/maintenance-construction" className="hover:text-gold transition-colors" data-testid="footer-link-maintconstruct">{t("nav.maintConstruct")}</Link></li>
                        <li><Link to="/concierge" className="hover:text-gold transition-colors" data-testid="footer-link-concierge">{t("nav.concierge")}</Link></li>
                        <li><Link to="/blog" className="hover:text-gold transition-colors" data-testid="footer-link-blog">{t("nav.blog")}</Link></li>
                        <li><Link to="/testimonials" className="hover:text-gold transition-colors" data-testid="footer-link-testimonials">{t("nav.testimonials")}</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <p className="overline text-gold/80 mb-5">{t("footer.contact")}</p>
                    <ul className="space-y-4 text-sm text-pearl/75">
                        <li className="flex items-start gap-3">
                            <MapPin className="w-4 h-4 text-gold mt-0.5" strokeWidth={1.25} />
                            <span>{t("footer.address")}</span>
                        </li>
                        <li className="flex items-center gap-3">
                            <Phone className="w-4 h-4 text-gold" strokeWidth={1.25} />
                            <a href="tel:+13107706585" onClick={() => gaPhoneClick({ cta_location: "footer" })} className="hover:text-gold transition-colors" data-testid="footer-phone">+1 (310) 770-6585</a>
                        </li>
                        <li className="flex items-center gap-3">
                            <Mail className="w-4 h-4 text-gold" strokeWidth={1.25} />
                            <a href="mailto:info@pacifickeymanagement.com" onClick={() => gaEmailClick({ cta_location: "footer" })} className="hover:text-gold transition-colors" data-testid="footer-email">info@pacifickeymanagement.com</a>
                        </li>
                    </ul>
                    <SocialLinks
                        label={t("footer.followUs")}
                        labelClass="overline text-gold/80 mt-8 mb-4"
                        iconClass="w-5 h-5"
                        linkClass="text-pearl/70 hover:text-gold transition-colors duration-300"
                    />
                </div>
            </div>

            <div className="border-t border-pearl/10">
                <div className="container-luxe py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[0.7rem] text-pearl/45 tracking-widest uppercase">
                    <span>© {year} Pacific Key Management · {t("footer.rights")}</span>
                    <Link to="/admin/login" className="hover:text-gold transition-colors" data-testid="footer-admin-link">Admin</Link>
                </div>
            </div>
        </footer>
    );
}
